import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const VrContext = createContext(null);

export function VrProvider({ children }) {
  const [vrSafe, setVrSafe] = useState(false);
  const [gazeActive, setGazeActive] = useState(false);
    const [rotate180, setRotate180] = useState(() => localStorage.getItem('vrRotate180') === '1');
    const [immersive, setImmersive] = useState(() => localStorage.getItem('vrImmersive') === '1');
  const [dwellProgress, setDwellProgress] = useState(0);
  const dwellTimerRef = useRef(null);
  const dwellCallbackRef = useRef(null);

  // Persist settings
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('vrSettings') || '{}');
      if (typeof saved.vrSafe === 'boolean') setVrSafe(saved.vrSafe);
      if (typeof saved.gazeActive === 'boolean') setGazeActive(saved.gazeActive);
    } catch {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('vrSettings', JSON.stringify({ vrSafe, gazeActive })); } catch {}
  }, [vrSafe, gazeActive]);

  // Body class for global styles
  useEffect(() => {
    const cls = 'vr-safe-global';
    if (vrSafe) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [vrSafe]);
  useEffect(() => {
    document.body.classList.toggle('vr-rotate-180', rotate180);
  }, [rotate180]);
  useEffect(() => {
    document.body.classList.toggle('vr-immersive', immersive);
  }, [immersive]);

  const startDwell = (cb, durationMs = 1000) => {
    if (!gazeActive) return;
    clearInterval(dwellTimerRef.current);
    setDwellProgress(0);
    dwellCallbackRef.current = cb;
    const steps = 20; // 50ms * 20 = 1000ms
    const stepMs = Math.max(20, Math.floor(durationMs / steps));
    let i = 0;
    dwellTimerRef.current = setInterval(() => {
      i += 1;
      const pct = Math.min(100, Math.round((i / steps) * 100));
      setDwellProgress(pct);
      if (i >= steps) {
        clearInterval(dwellTimerRef.current);
        const fn = dwellCallbackRef.current;
        setDwellProgress(0);
        dwellCallbackRef.current = null;
        try { fn && fn(); } catch {}
      }
    }, stepMs);
  };

  const clearDwell = () => {
    clearInterval(dwellTimerRef.current);
    setDwellProgress(0);
    dwellCallbackRef.current = null;
  };

  // Basic delegation: trigger dwell on buttons/links automatically
  useEffect(() => {
    if (!gazeActive) return;
    const over = (e) => {
      const t = e.target.closest('button, a[href], [role="button"], [data-gaze]');
      if (!t || t.hasAttribute('disabled')) return;
      startDwell(() => t.click());
    };
    const out = () => clearDwell();
    document.addEventListener('mouseover', over);
    document.addEventListener('mouseout', out);
    return () => {
      document.removeEventListener('mouseover', over);
      document.removeEventListener('mouseout', out);
      clearDwell();
    };
  }, [gazeActive]);

  const toggleRotate180 = () => {
    const next = !rotate180; setRotate180(next); localStorage.setItem('vrRotate180',''+(next?1:0));
  };
  // Compose VR context value including immersive/rotation and dwell controls
  const value = { vrSafe, setVrSafe, gazeActive, setGazeActive, rotate180, immersive, dwellProgress, startDwell, clearDwell, toggleRotate180, setImmersive };
  return <VrContext.Provider value={value}>{children}</VrContext.Provider>;
}

export function useVr() {
  const ctx = useContext(VrContext);
  if (!ctx) throw new Error('useVr must be used within VrProvider');
  return ctx;
}
